import { useMemo } from 'react';
import { erc20Abi } from 'viem';
import { useBalance, useReadContracts } from 'wagmi';

import { getBalanceRefreshInterval } from '@/constants/chain';

import { isEthereumAddress } from '@/utils/models';
import { isTokenAddressNativeAsset } from '@/utils/multichain';

type UseTokenBalanceParams = {
  address: string | undefined;
  tokenAddress: string | undefined;
  chainId: number | undefined;
};

export type EvmTokenBalance = { value: bigint; decimals: number };

/**
 * Reads an EVM account's native or ERC-20 balance for one token, polled at the
 * chain's balance refresh interval. The balance display, Max, and the
 * price-vs-quote gate all share this one query so they cannot disagree.
 */
export function useTokenBalance({ address, tokenAddress, chainId }: UseTokenBalanceParams) {
  const evmAccount = address != null && isEthereumAddress(address) ? address : undefined;
  const evmToken =
    tokenAddress != null && isEthereumAddress(tokenAddress) ? tokenAddress : undefined;
  const isNativeToken = isTokenAddressNativeAsset(tokenAddress, chainId);
  const refetchInterval = getBalanceRefreshInterval(chainId);

  // The native check is tri-state (undefined until the chain is known); never read against a fallback chain.
  const canRead = Boolean(evmAccount && evmToken && chainId != null);
  const fetchNative = canRead && isNativeToken === true;
  const fetchErc20 = canRead && isNativeToken === false;

  const { data: nativeBalance } = useBalance({
    address: fetchNative ? evmAccount : undefined,
    chainId,
    query: { refetchInterval },
  });
  const { data: erc20Reads } = useReadContracts({
    allowFailure: false,
    contracts:
      evmAccount && evmToken && fetchErc20
        ? [
            {
              address: evmToken,
              abi: erc20Abi,
              functionName: 'balanceOf',
              args: [evmAccount],
              chainId,
            },
            { address: evmToken, abi: erc20Abi, functionName: 'decimals', chainId },
          ]
        : [],
    query: { enabled: fetchErc20, refetchInterval },
  });

  const data = useMemo<EvmTokenBalance | undefined>(() => {
    if (nativeBalance) return { value: nativeBalance.value, decimals: nativeBalance.decimals };
    if (erc20Reads && erc20Reads.length === 2) {
      return { value: erc20Reads[0], decimals: erc20Reads[1] };
    }
    return undefined;
  }, [nativeBalance, erc20Reads]);

  return { data };
}
